import { FUND_CATEGORIES } from '../budgetPlanning.js';
import { dateString } from '../dateString.js';
import { classificationLabel, classificationsOf, extractLineItems, literalDate, literalStart, projectedDueDates, withinCap, } from '../payments/payments.js';
import { lineKeyId, normalizeCategoryText, } from './projection.js';
const MONTHS_A_YEAR = 12;
export const monthCoverageKey = (lineId, index) => `${lineId}\u0000${index}`;
export function budgetByTaxonomy(rows) {
    return new Map(rows.map((row) => [row.taxonomyId, row.amount]));
}
const monthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
function periodsFor(currentYear, horizonYears) {
    return Array.from({ length: horizonYears + 1 }, (_, i) => currentYear + i).flatMap((year) => Array.from({ length: 12 }, (__, m) => ({ fiscalYear: year, year, month: m + 1 })));
}
function classificationResolver(taxonomy, chargeTypes) {
    const byName = new Map(taxonomy.map((e) => [
        `${e.type}:${normalizeCategoryText(e.category)}:${normalizeCategoryText(e.subcategory)}`,
        e,
    ]));
    const byId = new Map(taxonomy.map((e) => [e.id, e]));
    const lineOf = (type, entry) => ({
        kind: 'taxonomy',
        taxonomyId: entry.id,
        type,
        category: entry.category,
        subcategory: entry.subcategory,
    });
    return (type, classification) => {
        if (classification === undefined)
            return { kind: 'uncategorized', type };
        const entry = classification.kind === 'taxonomy'
            ? byName.get(`${type}:${normalizeCategoryText(classification.category)}:${normalizeCategoryText(classification.subcategory)}`)
            : byId.get(chargeTypes.get(classification.chargeType) ?? -1);
        return entry !== undefined && entry.type === type
            ? lineOf(type, entry)
            : { kind: 'unmapped', type, categoryText: classificationLabel(classification) };
    };
}
const EARLIEST = dateString('1900-01-01');
function repeatsOf(payment) {
    switch (payment.kind) {
        case 'zero':
        case 'once':
            return [];
        case 'repeat':
            return [payment];
        case 'all':
        case 'first':
            return payment.payments.flatMap(repeatsOf);
        case 'notToExceed':
            return repeatsOf(payment.payment);
        default: {
            const _exhaustive = payment;
            throw new Error(`Unhandled payment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export function scheduleCoverage(input, expansion, include = () => true) {
    return new Set(input.schedules
        .filter(({ ref }) => include(ref))
        .flatMap(({ ref, spec }) => repeatsOf(spec).flatMap((payment) => {
        const lines = classificationsOf(payment).flatMap((classification) => {
            const key = expansion.lineOf(ref.direction, classification);
            return key.kind === 'taxonomy' ? [lineKeyId(key)] : [];
        });
        return projectedDueDates(payment, expansion.windowStart, expansion.windowEnd, input.asOfDate).flatMap((dueDate) => {
            const index = expansion.periodIndexOf(dueDate.slice(0, 7));
            return index === undefined ? [] : lines.map((line) => monthCoverageKey(line, index));
        });
    })));
}
function occurrencesOf(payment, windowStart, windowEnd, asOfDate) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'all':
            return payment.payments.flatMap((p) => occurrencesOf(p, windowStart, windowEnd, asOfDate));
        case 'first': {
            for (const p of payment.payments) {
                const found = occurrencesOf(p, windowStart, windowEnd, asOfDate);
                if (found.length > 0)
                    return found;
            }
            return [];
        }
        case 'once':
        case 'repeat':
            return projectedDueDates(payment, windowStart, windowEnd, asOfDate).flatMap((dueDate) => {
                const lines = extractLineItems(payment.amount, { currentDate: dueDate, dueDate });
                return lines.length === 0 ? [] : [{ dueDate, lines, term: leafTermOf(payment) }];
            });
        case 'notToExceed': {
            const inner = occurrencesOf(payment.payment, EARLIEST, windowEnd, asOfDate).map((occurrence) => {
                const total = occurrence.lines.reduce((sum, line) => sum + line.amount, 0);
                return { ...occurrence, total, amount: total };
            });
            return withinCap(inner, payment.maxAmount, payment.period).map(({ dueDate, lines, term, total, amount }) => ({
                dueDate,
                term,
                lines: amount === total
                    ? lines
                    : lines.map((line) => ({ ...line, amount: (line.amount * amount) / total })),
            }));
        }
        default: {
            const _exhaustive = payment;
            throw new Error(`Unhandled payment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
const isOpeningBalance = (line) => line.classification?.kind === 'chargeType' &&
    line.classification.chargeType === 'opening-balance';
function leafTermOf(payment) {
    if (payment.kind === 'once') {
        return { kind: 'once', on: literalDate(payment.date) };
    }
    const monthly = payment.recurrence.kind === 'monthly' && payment.recurrence.interval === 1;
    return {
        kind: monthly ? 'monthly' : 'repeating',
        from: literalStart(payment),
        until: payment.endDate,
    };
}
export function termsOf(payment) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'once':
        case 'repeat':
            return [leafTermOf(payment)];
        case 'all':
        case 'first':
            return payment.payments.flatMap(termsOf);
        case 'notToExceed':
            return termsOf(payment.payment);
        default: {
            const _exhaustive = payment;
            throw new Error(`Unhandled payment: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
function framePeriods(input) {
    const currentYear = Number(input.asOfDate.slice(0, 4));
    const periods = periodsFor(currentYear, input.horizonYears);
    return {
        periods,
        windowStart: dateString(`${currentYear}-01-01`),
        windowEnd: dateString(`${currentYear + input.horizonYears}-12-31`),
        periodIndex: new Map(periods.map((p, i) => [monthKey(p.year, p.month), i])),
    };
}
export function expandProjection(input) {
    const { periods, windowStart, windowEnd, periodIndex } = framePeriods(input);
    const resolve = classificationResolver(input.taxonomy, input.chargeTypes ?? new Map());
    const valued = new Set();
    const occurrences = input.schedules.flatMap(({ ref, spec }) => occurrencesOf(spec, windowStart, windowEnd, input.asOfDate).flatMap((due) => {
        if (due.dueDate < windowStart)
            return [];
        const index = periodIndex.get(due.dueDate.slice(0, 7));
        if (index === undefined)
            return [];
        return due.lines.flatMap((item) => {
            if (isOpeningBalance(item))
                return [];
            const key = resolve(ref.direction, item.classification);
            valued.add(monthCoverageKey(lineKeyId(key), index));
            return item.amount === 0
                ? []
                : [{ ref, dueDate: due.dueDate, term: due.term, item, key, index }];
        });
    }));
    const frame = {
        windowStart,
        windowEnd,
        periodIndexOf: (month) => periodIndex.get(month),
        lineOf: resolve,
    };
    for (const key of scheduleCoverage(input, frame))
        valued.add(key);
    return { occurrences, periods, valued, ...frame };
}
export function buildProjection(input) {
    const expansion = expandProjection(input);
    return sumProjection(input, expansion.occurrences, expansion.valued);
}
export function sumProjection(input, occurrences, valued = new Set()) {
    const { periods } = framePeriods(input);
    const asOfMonth = input.asOfDate.slice(0, 7);
    const lines = new Map();
    const funds = new Map();
    const issues = new Map();
    const addIssue = (id, issue) => {
        if (!issues.has(id))
            issues.set(id, issue);
    };
    for (const { ref, term, item, key, index } of occurrences) {
        if (key.kind === 'unmapped') {
            addIssue(`unmapped:${ref.displayId}:${key.categoryText}`, {
                kind: 'unmapped-category',
                schedule: ref,
                categoryText: key.categoryText,
            });
        }
        if (key.kind === 'uncategorized') {
            addIssue(`uncategorized:${ref.displayId}`, {
                kind: 'uncategorized-schedule',
                schedule: ref,
            });
        }
        const id = lineKeyId(key);
        const line = lines.get(id) ?? {
            key,
            scheduled: periods.map(() => 0),
            inflationExemptScheduled: periods.map(() => 0),
        };
        line.scheduled[index] = (line.scheduled[index] ?? 0) + item.amount;
        if (ref.inflationExempt) {
            line.inflationExemptScheduled[index] =
                (line.inflationExemptScheduled[index] ?? 0) + item.amount;
        }
        lines.set(id, line);
        if (key.kind === 'taxonomy' && key.type === 'income' && FUND_CATEGORIES.has(key.category)) {
            const name = item.description.trim() === '' ? ref.name : item.description.trim();
            const fundId = `${id}|${name}`;
            const fund = funds.get(fundId) ?? { key, name, schedules: new Map() };
            const schedule = fund.schedules.get(ref.displayId) ?? {
                displayId: ref.displayId,
                term,
                scheduled: periods.map(() => 0),
            };
            schedule.scheduled[index] = (schedule.scheduled[index] ?? 0) + item.amount;
            fund.schedules.set(ref.displayId, schedule);
            funds.set(fundId, fund);
        }
    }
    const recordedById = new Map((input.actuals ?? []).map((a) => [lineKeyId(a.key), a]));
    const blankLine = (key) => ({
        key,
        scheduled: periods.map(() => 0),
        inflationExemptScheduled: periods.map(() => 0),
    });
    for (const [id, recorded] of recordedById) {
        if (!lines.has(id))
            lines.set(id, blankLine(recorded.key));
    }
    for (const entry of input.taxonomy) {
        if (!(input.budget?.has(entry.id) ?? false))
            continue;
        const key = {
            kind: 'taxonomy',
            taxonomyId: entry.id,
            type: entry.type,
            category: entry.category,
            subcategory: entry.subcategory,
        };
        const id = lineKeyId(key);
        if (!lines.has(id))
            lines.set(id, blankLine(key));
    }
    const currentFiscalYear = periods[0]?.fiscalYear;
    const restOfYear = periods.flatMap((period, i) => period.fiscalYear === currentFiscalYear && monthKey(period.year, period.month) >= asOfMonth
        ? [i]
        : []);
    const result = [...lines.entries()].map(([id, line]) => {
        const recorded = recordedById.get(id)?.byMonth;
        const scheduleLeft = restOfYear.some((i) => (line.scheduled[i] ?? 0) !== 0 || valued.has(monthCoverageKey(id, i)));
        const yearBudget = line.key.kind === 'taxonomy' && !scheduleLeft
            ? input.budget?.get(line.key.taxonomyId)
            : undefined;
        const accrued = yearBudget === undefined ? undefined : yearBudget / MONTHS_A_YEAR;
        const cells = periods.map((period, i) => {
            const key = monthKey(period.year, period.month);
            const actual = key >= asOfMonth
                ? undefined
                : (recorded?.get(key) ?? (input.closedMonths?.has(key) ? 0 : undefined));
            if (actual !== undefined)
                return { kind: 'actual', amount: actual };
            if (accrued !== undefined && restOfYear.includes(i)) {
                return { kind: 'accrued', amount: accrued };
            }
            return { kind: 'scheduled', amount: line.scheduled[i] ?? 0 };
        });
        const inflationExemptScheduled = line.inflationExemptScheduled.some((amount) => amount !== 0)
            ? line.inflationExemptScheduled
            : undefined;
        return {
            key: line.key,
            cells,
            ...(inflationExemptScheduled === undefined ? {} : { inflationExemptScheduled }),
        };
    });
    return {
        asOfDate: input.asOfDate,
        periods,
        lines: result,
        issues: [...issues.values()],
        fundSources: [...funds.values()].map(({ schedules, ...fund }) => ({
            ...fund,
            schedules: [...schedules.values()],
        })),
    };
}
//# sourceMappingURL=buildProjection.js.map